import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner, faMugHot } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Button } from '../../ui';
import {
    listCoffeeMasterRequests,
    approveCoffeeMasterRequest,
    rejectCoffeeMasterRequest,
    CoffeeMasterRequest,
} from '../coffee_card_web/api/coffeeApi';

type Filter = 'pending' | 'all';

// userId is populated on the queue rows, but the contract allows a bare id string too —
// guard with a typeof check before reaching into the object (same pattern as the *Ref shapes).
function userEmojiOf(req: CoffeeMasterRequest): string | undefined {
    return typeof req.userId === 'object' && req.userId ? req.userId.userEmoji : undefined;
}

function emailOf(req: CoffeeMasterRequest): string | undefined {
    return typeof req.userId === 'object' && req.userId ? req.userId.email : undefined;
}

function formatDate(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
}

const STATUS_BADGE: Record<CoffeeMasterRequest['status'], string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-gray-100 text-gray-800',
};

const CoffeeMasterRequestsAdmin: React.FC = () => {
    const [requests, setRequests] = useState<CoffeeMasterRequest[]>([]);
    const [pending, setPending] = useState(0);
    const [loading, setLoading] = useState(true);
    const [actingId, setActingId] = useState<string | null>(null);
    const [filter, setFilter] = useState<Filter>('pending');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { requests: rows, pending: pendingCount } = await listCoffeeMasterRequests(filter);
            setRequests(rows);
            setPending(pendingCount);
        } catch (err) {
            toast.error('Failed to load Coffee Master requests');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const approve = async (id: string) => {
        setActingId(id);
        try {
            await approveCoffeeMasterRequest(id);
            toast.success('Coffee Master approved');
            fetchData();
        } catch {
            toast.error('Failed to approve');
        } finally {
            setActingId(null);
        }
    };

    const reject = async (id: string) => {
        setActingId(id);
        try {
            await rejectCoffeeMasterRequest(id);
            toast.success('Request rejected');
            fetchData();
        } catch {
            toast.error('Failed to reject');
        } finally {
            setActingId(null);
        }
    };

    return (
        <div className="container mx-auto p-4 max-w-3xl">
            <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faMugHot} className="text-muted-foreground" />
                Coffee Masters
            </h1>
            <p className="text-sm text-muted-foreground mb-4">Review access requests for Coffee Master setup.</p>

            <div className="flex items-center justify-between gap-2 mb-4">
                <span className="text-sm">{pending} pending</span>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant={filter === 'pending' ? 'default' : 'outline'}
                        onClick={() => setFilter('pending')}
                    >
                        Pending
                    </Button>
                    <Button
                        size="sm"
                        variant={filter === 'all' ? 'default' : 'outline'}
                        onClick={() => setFilter('all')}
                    >
                        All
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : requests.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                    {filter === 'pending' ? 'No pending Coffee Master requests' : 'No Coffee Master requests'}
                </p>
            ) : (
                <div className="space-y-2">
                    {requests.map((req) => {
                        const isActing = actingId === req._id;
                        const isPending = req.status === 'pending';
                        const emoji = userEmojiOf(req);
                        const email = emailOf(req);

                        return (
                            <div
                                key={req._id}
                                className="p-3 border border-ink/15 rounded-lg bg-paper space-y-3"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium flex items-center gap-2">
                                            {emoji && <span className="flex-shrink-0">{emoji}</span>}
                                            {req.username}
                                        </div>
                                        {email && (
                                            <div className="text-xs text-muted-foreground mt-1">{email}</div>
                                        )}
                                        {req.message && (
                                            <div className="text-sm mt-2 whitespace-pre-wrap">{req.message}</div>
                                        )}
                                        <div className="text-xs text-muted-foreground mt-1">
                                            requested {formatDate(req.createdAt)}
                                            {req.reviewedAt && ` · reviewed ${formatDate(req.reviewedAt)}`}
                                        </div>
                                    </div>
                                    {!isPending && (
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${STATUS_BADGE[req.status]}`}
                                        >
                                            {req.status}
                                        </span>
                                    )}
                                </div>

                                {isPending && (
                                    <div className="flex flex-wrap gap-2 pt-1 border-t border-ink/10">
                                        <Button size="sm" onClick={() => approve(req._id)} disabled={isActing}>
                                            {isActing ? (
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                                            ) : (
                                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                            )}
                                            Approve
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => reject(req._id)}
                                            disabled={isActing}
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                            Reject
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CoffeeMasterRequestsAdmin;
